<template>
  <div>
    <div>
      <v-dialog
          v-model="viewSheet"
          fullscreen
          hide-overlay
          transition="scale-transition"
      >
        <InspectionSheet
            v-if="viewSheet"
            :inspection-id="inspectionId"
            @close="viewSheet = false"
        ></InspectionSheet>
      </v-dialog>
    </div>
    <ejs-schedule
        id="scheduler"
        ref="scheduler"
        :currentView="currentView"
        :enableAdaptiveUI="false"
        :eventClick="onEventClick"
        :eventRendered="onEventRendered"
        :eventSettings="eventSettings"
        :group="{resources: ['inspectors'], enableCompactView: false}"
        :rowAutoHeight="true"
        :selectedDate.sync="selectedDate"
        height="100%"
        quickInfoOnSelectionEnd="true"
        @actionBegin="actionBegin"
        @actionComplete="actionComplete"
        @dragStart="helper.dragStart($event)"
        @dragStop="helper.dragStop($event)"
        @popupOpen="helper.onPopupOpen($event)"
        @resizeStart="helper.resizeStart($event)"
        @update:currentView="$emit('input', $event)"
    >
      <e-views>
        <e-view
            :endHour="endTime"
            :startHour="startTime"
            :timeScale="{interval: 60, slotCount: 60 / increments}"
            displayName="Day"
            option="Day"
        ></e-view>
        <e-view
            :endHour="endTime"
            :startHour="startTime"
            :timeScale="{interval: 60, slotCount: 60 / increments}"
            displayName="Week"
            option="Week"
        ></e-view>
        <e-view
            displayName="Month"
            option="Month"
        ></e-view>
        <e-view
            :endHour="endTime"
            :startHour="startTime"
            :timeScale="{interval: 60, slotCount: 60 / increments}"
            displayName="Timeline Day"
            option="TimelineDay"
        ></e-view>
        <e-view
            :timeScale="{interval: 60, slotCount: 60 / increments}"
            displayName="Timeline Week"
            option="TimelineWeek"
        ></e-view>
        <e-view
            displayName="Timeline Month"
            option="TimelineMonth"
        ></e-view>
      </e-views>
      <e-resources>
        <e-resource
            :allowMultiple='allowMultiple'
            :dataSource='inspectors'
            field='InspectorId'
            idField='id'
            name='inspectors'
            textField='display_name'
            title='Inspector'
        >
        </e-resource>
      </e-resources>
    </ejs-schedule>
  </div>
</template>

<script>
import {Day} from '@syncfusion/ej2-schedule/src/schedule/renderer/day'
import {Week} from '@syncfusion/ej2-schedule/src/schedule/renderer/week'
import {Month} from '@syncfusion/ej2-schedule/src/schedule/renderer/month'
import {DragAndDrop} from '@syncfusion/ej2-schedule/src/schedule/actions/drag'
import {Resize} from '@syncfusion/ej2-schedule/src/schedule/actions/resize'
import {TimelineMonth} from '@syncfusion/ej2-schedule/src/schedule/renderer/timeline-month'
import {TimelineViews} from '@syncfusion/ej2-schedule/src/schedule/renderer/timeline-view'
import {mapActions, mapGetters, mapMutations} from 'vuex'
import {AVAILABLE, NOT_AVAILABLE, SchedulerHelper} from "../helpers/SchedulerHelper"
import debounce from 'underscore/modules/debounce.js'
import format from "date-fns/format";
import Vue from 'vue';
import { SchedulePlugin } from '@syncfusion/ej2-vue-schedule';
Vue.use(SchedulePlugin);

const InspectionSheet = () => import('./InspectionSheet')

export default {
  name: 'AppSchedulerComponent',
  components: {InspectionSheet},
  props: [
      'inspectors', 'inspectorColors', 'startTime', 'endTime', 'increments', 'currentView', 'viewAvailability'
  ],
  provide: {
    schedule: [TimelineViews, TimelineMonth, Resize, DragAndDrop, Day, Week, Month]
  },
  data() {
    return {
      helper: {},
      allowMultiple: true,
      selectedDate: new Date(),
      eventSettings: {
        spannedEventPlacement: 'TimeSlot'
      },
      viewSheet: false,
      inspectionId: null,
    }
  },
  computed: {
    ...mapGetters(['getUser', 'inspectionSheetOpen', 'platform']),
  },
  methods: {
    ...mapActions(['retrieveEvents', 'postCalendarEvent', 'putCalendarEvent', 'deleteCalendarEvent']),
    ...mapMutations(['setInspectionSheetOpen']),
    getEvents: debounce(async function () {
      const viewDates = this.scheduler.getCurrentViewDates()
      const range = {
        start: format(viewDates[0], 'y-MM-dd 00:00:00'),
        end: format(viewDates[viewDates.length - 1], 'y-MM-dd 23:59:59')
      }
      this.scheduler.eventSettings.dataSource = (await this.retrieveEvents(range)).data
    }, 200),
    onEventClick(event) {
      // If this is an availability event then we don't allow it
      // to be edited, we create a new event instead
      if (event.event.EventType === 1) {
        return this.availabilityClick(event)
      }
      if (event.event.EventType === null) {
        event.cancel = true
        this.inspectionId = event.event.Id.substring(6)
        this.viewSheet = true
      }
    },
    onEventRendered(args) {
      if (! this.viewAvailability && [AVAILABLE, NOT_AVAILABLE].includes(args.data.EventType)) {
        args.cancel = true
        return
      }
      const inspectorId = this.inspectors[args.element.dataset.groupIndex].id
      this.helper.onEventRendered(args, this.inspectorColors[inspectorId])
    },
    actionBegin(event) {
      if (event.requestType === 'dateNavigate' && event.event?.type === 'touchmove') {
        event.cancel = true
        return
      }
      const method = event.requestType
      if (typeof this[method] === 'function') {
        return this[method](event)
      }
    },
    toolbarItemRendering(args) {
      args.items.splice(8, 0, {
        "align": "Right",
        "type": "Separator",
        "cssClass": "e-schedule-seperator"
      })
    },
    availabilityClick(event) {
      event.cancel = true
      const newEvent = {
        StartTime: event.event.StartTime,
        EndTime: event.event.EndTime,
        Subject: event.event.Subject,
        InspectorId: event.event.InspectorId,
      }
      this.scheduler.openEditor(newEvent, 'Add', true)
    },
    async eventCreate(calendarEvent) {
      let newEvent = calendarEvent.addedRecords[0]
      newEvent.IsAllDay = false
      newEvent.EventType = newEvent.EventType || NOT_AVAILABLE
      // Create the event optimistically. We'll change the id once we get it back from the server
      if (newEvent.Subject === 'Add title') {
        newEvent.Subject = ''
      }
      try {
        const response = await this.postCalendarEvent(newEvent)
        this.scheduler.eventSettings.dataSource
            .find(e => e.Id === newEvent.Id).Id = response.data.Id
      } catch (e) {
        this.scheduler.eventSettings.dataSource
            .splice(this.scheduler.eventSettings.dataSource.indexOf(newEvent), 1)
      } finally {
        this.scheduler.refreshEvents()
      }
    },
    eventChange(calendarEvent) {
      const changed = calendarEvent.changedRecords[0];
      if (changed.Subject === 'Add title') {
        changed.Subject = ''
      }
      this.putCalendarEvent(changed)
    },
    eventRemove(calendarEvent) {
      this.deleteCalendarEvent(calendarEvent.data[0])
    },
    actionComplete() {
    },
  },
  watch: {
    currentView() {
      this.getEvents()
    },
    selectedDate() {
      this.getEvents()
    },
    viewSheet(value) {
      this.setInspectionSheetOpen(value)
    },
    viewAvailability() {
      this.scheduler.refreshEvents()
    }
  },
  mounted() {
    this.scheduler = document.querySelector('#scheduler').ej2_instances[0]
    const isMobile = this.platform === 'mobile'
    this.scheduler.enableAdaptiveUI = isMobile
    this.scheduler.isAdaptive = isMobile
  },
  created() {
    this.helper = new SchedulerHelper(this)
  }
}
</script>

<style scoped>
#scheduler {
  overflow: visible;
}
</style>

<style>
@import '~@syncfusion/ej2-base/styles/material.css';
@import '~@syncfusion/ej2-buttons/styles/material.css';
@import '~@syncfusion/ej2-calendars/styles/material.css';
@import '~@syncfusion/ej2-dropdowns/styles/material.css';
@import '~@syncfusion/ej2-inputs/styles/material.css';
@import '~@syncfusion/ej2-navigations/styles/material.css';
@import '~@syncfusion/ej2-popups/styles/material.css';
@import '~@syncfusion/ej2-vue-schedule/styles/material.css';
#scheduler_dialog_wrapper .e-location-container {
  display: none;
}
.e-subject-container {
  width: 100% !important;
}
.custom-field-container {
  padding-top: 20px;
}
.e-interval {
  display: none !important;
}
.e-non-week, .e-days {
  display: none;
}
.e-InspectorId-container {
  display: none;
}
.v-color-picker__controls {
  display: none !important;
}
.v-color-picker__color {
  width: 30px;
  height: 30px;
  max-height: 30px;
}
.e-all-day-time-zone-row {
  display: none !important;
}
.e-content-wrap {
  height: auto !important;
}
.e-ddl.e-popup.e-lib.e-control.e-ddl-device.e-popup-open {
  position: fixed;
}
div.e-repeat-parent-row > div > div > label > span.e-label {
  font-size: 0.8125rem;
  margin-left: 5px !important;
}
</style>